// Define frame rates as fractions to represent NTSC (National Television System Committee) standards
import { roundToDecimals } from '@iconik-io/core/src/round_to_decimals';

const FPS_2997 = 30000 / 1001; // 29.97 fps
const FPS_5994 = 60000 / 1001; // 59.94 fps

const dropFrameRates = [FPS_2997, FPS_5994];

// A small number used to compare floating point values accurately
const EPSILON = 0.0005;

// A set of supported NTSC drop frame rates
const supportedDropFrameRates = new Set(dropFrameRates);

const isCloseToValue = (value: number, target: number) =>
  Math.abs(value - target) / target < EPSILON;

// Function to get the nearest standard frame rate to a given value.
// In case if it matches some drop frame rate, it will return the nearest supported drop frame rate.
// Otherwise, it will return ceil rounded frame rate.
const normalizeDropFrameRate = (frameRate: number): number => {
  for (const dropFrameRate of dropFrameRates) {
    if (isCloseToValue(frameRate, dropFrameRate)) {
      return dropFrameRate;
    }
  }
  return Math.ceil(frameRate);
};

const normalizeFrameRate = (frameRate: number): number => {
  const normalizedDropFrameRate = normalizeDropFrameRate(frameRate);
  const isNearDefaultRate = normalizedDropFrameRate !== Math.ceil(frameRate);

  return isNearDefaultRate ? Math.ceil(frameRate) : normalizedDropFrameRate;
};

const isDropFrameRate = (frameRate: number): boolean => {
  const normalizedDropFrameRate = normalizeDropFrameRate(frameRate);
  return supportedDropFrameRates.has(normalizedDropFrameRate);
};

export type TimecodeOptions = {
  frameRate: number;

  // If specified frame rate is not compatible with drop frame, drop frame will be
  // false regardless of this value.
  isDropFrame?: boolean;

  // If true, timecode frames part will be treated as if it's milliseconds value.
  // Basically it forces frame rate to be 1000, drop frame to be false and
  // last SMPTE separator to be "."
  isMillisecondsMode?: boolean;
};

export type TimecodeInitValue =
  | {
      frames: number;
    }
  | {
      seconds: number;
    }
  | {
      smpte: string;
    };

type TimecodeParts = {
  hours: string;
  minutes: string;
  seconds: string;
  frames: string;
  lastSeparator: string;
};

export class Timecode {
  #frames: number;
  get frames(): number {
    return this.#frames;
  }

  // The frame rate normalized based on the input frame rate and drop frame considerations.
  // This is the effective frame rate used for all timecode calculations within the class.
  readonly #normalizedFrameRate: number;

  readonly #isDropFrame: boolean;
  get isDropFrame(): boolean {
    return this.#isDropFrame;
  }

  // The integer part of the normalized frame rate.
  // This is used in calculations where a whole number frame rate is necessary,
  // such as in the conversion between drop and non-drop frame formats.
  readonly #integerFrameRate: number;

  // The original frame rate as provided in the options.
  // This represents the unmodified frame rate before any normalization or adjustments
  // and is used primarily for reference or initial calculations.
  readonly #originalFrameRate: number;

  readonly #isMillisecondsMode: boolean;

  // Create a new Timecode instance.
  // initValue can be an object with frames, seconds or smpte property.
  // If initValue is not specified, it will be set to 0.
  // The main property representing current time is frames.
  constructor(
    options: TimecodeOptions,
    initValue: TimecodeInitValue = { frames: 0 },
  ) {
    this.#isMillisecondsMode = options.isMillisecondsMode ?? false;

    let frameRate = options.frameRate;
    let isDropFrame = options.isDropFrame ?? false;
    if (this.#isMillisecondsMode) {
      frameRate = 1000;
      isDropFrame = false;
    }

    this.#originalFrameRate = frameRate;
    this.#normalizedFrameRate = normalizeFrameRate(this.#originalFrameRate);

    this.#integerFrameRate = Math.round(this.#normalizedFrameRate);

    this.#isDropFrame = isDropFrame && isDropFrameRate(this.#originalFrameRate);
    this.#frames = this.#getFrames(initValue);
  }

  #secondsToFrames(seconds: number): number {
    return Math.floor(seconds * this.#originalFrameRate);
  }

  // Convert initValue to frames number.
  #getFrames(initValue: TimecodeInitValue): number {
    if ('frames' in initValue) {
      return initValue.frames;
    } else if ('seconds' in initValue) {
      return Math.round(initValue.seconds * this.#originalFrameRate);
    } else if ('smpte' in initValue) {
      return this.#calculateFramesFromSMPTE(initValue.smpte);
    }
    return 0;
  }

  #framesPerMinute(): number {
    return 60 * this.#integerFrameRate;
  }

  // Convert frames number from non-drop frame to drop frame variant.
  #convertNonDropFrameToDrop(frames: number): number {
    const dropCount = this.#integerFrameRate === 60 ? 4 : 2;
    const framesPerMinute = this.#framesPerMinute();
    let result = frames;
    result -= dropCount * Math.floor(frames / framesPerMinute);
    result += dropCount * Math.floor(frames / (10 * framesPerMinute));

    return result;
  }

  // Convert frames number from drop frame to non-drop frame variant.
  #convertDropFrameToNonDrop(frames: number): number {
    const frameRate = this.#integerFrameRate;
    const dropCount = frameRate === 60 ? 4 : 2;
    const framesPerTenMinutes = 10 * 60 * frameRate - 9 * dropCount;
    const framesPerMinute = 60 * frameRate;
    const correctionFactor =
      dropCount * Math.floor((frames - dropCount) / framesPerTenMinutes);
    let result = frames;
    result +=
      dropCount *
      Math.floor(
        (frames - dropCount - correctionFactor) / (framesPerMinute - dropCount),
      );
    result -=
      dropCount * Math.floor(result / (10 * framesPerMinute + dropCount));

    return result;
  }

  setSeconds(seconds: number): this {
    this.#frames = this.#secondsToFrames(seconds);
    return this;
  }

  // Set current time from SMPTE timecode string.
  setSMPTE(smpte: string): this {
    this.#frames = this.#calculateFramesFromSMPTE(smpte);
    return this;
  }

  addFrames(frameCount: number): this {
    this.#frames += frameCount;
    return this;
  }

  subtractFrames(frameCount: number): this {
    this.#frames -= frameCount;
    return this;
  }

  #adjustToValidDropFrame(
    hours: number,
    minutes: number,
    seconds: number,
    frames: number,
  ): [hours: number, minutes: number, seconds: number, frames: number] {
    const frameRate = this.#integerFrameRate;
    const dropCount = frameRate === 60 ? 4 : 2;
    return minutes % 10 !== 0 && seconds === 0 && frames < dropCount
      ? [hours, minutes, seconds, dropCount]
      : [hours, minutes, seconds, frames];
  }

  // Parse SMPTE timecode string and return the number of frames.
  #calculateFramesFromSMPTE(smpte: string): number {
    const parts = smpte.split(/[:;.]/).map((part) => parseInt(part, 10));
    const [hours, minutes, seconds, frames] = this.#isDropFrame
      ? this.#adjustToValidDropFrame(parts[0], parts[1], parts[2], parts[3])
      : parts;

    let result =
      frames +
      (seconds + minutes * 60 + hours * 3600) * this.#normalizedFrameRate;

    if (this.#isDropFrame) {
      result = this.#convertNonDropFrameToDrop(result);
    }
    return Math.floor(result);
  }

  toSeconds(): number {
    if (this.#originalFrameRate <= 0) {
      return 0;
    }
    return roundToDecimals(this.#frames / this.#originalFrameRate, 5); // Rounding to avoid floating point errors
  }

  // Convert current time to an object with hours, minutes, seconds, frames and lastSeparator properties.
  // lastSeparator is ";" for drop frame and ":" for non-drop frame.
  toParts(): TimecodeParts {
    let frames = this.#frames;
    let separator = this.#isDropFrame ? ';' : ':';
    if (this.#isMillisecondsMode) {
      separator = '.';
    }
    frames = this.#isDropFrame
      ? this.#convertDropFrameToNonDrop(frames)
      : frames;

    const frameRate = this.#normalizedFrameRate;

    if (frameRate <= 0) {
      return {
        hours: '00',
        minutes: '00',
        seconds: '00',
        frames: '00',
        lastSeparator: separator,
      };
    }

    const hours = Math.floor(frames / (3600 * frameRate));
    const minutes = Math.floor(
      (frames % (3600 * frameRate)) / (60 * frameRate),
    );
    const seconds = Math.floor(
      ((frames % (3600 * frameRate)) % (60 * frameRate)) / frameRate,
    );
    frames = Math.floor(
      ((frames % (3600 * frameRate)) % (60 * frameRate)) % frameRate,
    );

    const frameDigitCount = frameRate < 100 ? 2 : 3;

    return {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
      frames: String(frames).padStart(frameDigitCount, '0'),
      lastSeparator: separator,
    };
  }

  // Convert current time to frame number. For frames in video we use 1-based counting, unless you're in SMPTE
  toFrames(): number {
    return this.#frames + 1;
  }

  // Convert current time to SMPTE timecode string.
  toSMPTE(): string {
    const { hours, minutes, seconds, frames, lastSeparator } = this.toParts();
    return `${hours}:${minutes}:${seconds}${lastSeparator}${frames}`;
  }
}
